import { useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactECharts from 'echarts-for-react';
import { LongArrowUp, LongArrowDown, InfoIcon } from '../CommonIcons/Icons';
import { ECHO_CHART_CONFIG, Y_AXIS_STATUS_CONFIG, LINE_STYLE_CONFIG, getYAxisJobsConfig, SERIES_COLOR_MAP } from '../PipelineRunStatus/constants/chartConstants';
import { OvertimeSeries, OvertimeCard, formatNumber } from '../PipelineRunStatus/utils/pipelineChartUtils';
import LoaderComponent from 'components/Loader';

interface Props {
  title: string;
  xAxisData: string[];
  series: OvertimeSeries[];
  cards?: OvertimeCard[];
  isCardsLoading?: boolean;
  isLoading?: boolean; 
}

const formatTooltipParam = (p: any, series: OvertimeSeries[]): string => {
  const seriesItem = series.find((s) => s.name === p.seriesName);
  const raw = seriesItem?.rawData?.[p.dataIndex];
  const percent = p.value;
  if (p.seriesName === 'Total') {
    return `${p.marker} ${p.seriesName}: ${
      raw === undefined ? '-' : formatNumber(raw)
    }`;
  }

  return `${p.marker} ${p.seriesName}: ${raw === undefined ? '-' : formatNumber(raw)} (${percent?.toFixed(2)}%)`;
};

const formatSeriesItem = (s: OvertimeSeries): any => ({
  ...s,
  ...LINE_STYLE_CONFIG,
  itemStyle: { color: SERIES_COLOR_MAP[s.name] || '#999999' },
  yAxisIndex: s.name === 'Total' ? 1 : 0,
});

type StatCardProps = OvertimeCard & {
  isLoading?: boolean;
};

const StatCard = ({ label, value, subtitle, trend, tooltip, arrowOnly, isLoading }: StatCardProps) => {
  const theme = useTheme();
  const upColor = '#E53935';
  const downColor = theme.palette.success.main;

  let trendColor: string;
  if (trend === 'up') {
    trendColor = upColor;
  } else if (trend === 'down') {
    trendColor = downColor;
  } else {
    trendColor = theme.palette.text.subtle;
  }

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1,
        backgroundColor: theme.palette.kpiTile.background.card,
        width: 300,
        minHeight: subtitle ? 145 : 'auto',
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography
          sx={{
            flex: 1,
            fontSize: '1.25rem',
            fontWeight: 500,
            color: theme.palette.text.primary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Typography>

        {tooltip && (
          <Tooltip title={tooltip} placement="top">
            <Box sx={{ display: 'inline-flex', cursor: 'help' }}>
              <InfoIcon size={26} color={theme.palette.info.main} />
            </Box>
          </Tooltip>
        )}
      </Box>

      {isLoading ? (
        <Box
          sx={{
            height: 72,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LoaderComponent size={28} />
        </Box>
      ) : (
        <>
          {arrowOnly ? (
            <Box sx={{ py: 1 }}>
              {trend === 'up' && <LongArrowUp color={upColor} size={43} />}
              {trend === 'down' && <LongArrowDown color={downColor} size={43} />}
            </Box>
          ) : (
            <Typography
              sx={{
                fontSize: '3rem',
                fontWeight: 400,
                lineHeight: 1.1,
                color: theme.palette.kpiTile.text.value,
                mb: subtitle ? 0.75 : 0,
              }}
            >
              {value}
            </Typography>
          )}
        </>
      )}

      {!isLoading && subtitle && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
          {!arrowOnly && trend === 'up' && (
            <Box
              component="span"
              sx={{ cursor: 'help', flexShrink: 0, lineHeight: 0, display: 'inline-flex' }}
            >
              <LongArrowUp color={upColor} size={28} />
            </Box>
          )}
          {!arrowOnly && trend === 'down' && (
            <Box
              component="span"
              sx={{ cursor: 'help', flexShrink: 0, lineHeight: 0, display: 'inline-flex' }}
            >
              <LongArrowDown color={downColor} size={28} />
            </Box>
          )}

          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 500,
              lineHeight: 1.2,
              color: trendColor,
              overflow: 'hidden',
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const TrendLineInfoWidget = ({
  title,
  xAxisData,
  series,
  cards,
  isCardsLoading,
  isLoading = false
}: Props) => {

  const chartOption = useMemo(() => {
    const allSeriesData = series.flatMap((s) => s.data);
    const maxValue = allSeriesData.length > 0 ? Math.max(...allSeriesData) : 0;
    const yAxisMaxValue = Math.ceil((maxValue * 1.1) / 100) * 100;

    return {
      ...ECHO_CHART_CONFIG,
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) =>
          params.map((p: any) => formatTooltipParam(p, series)).join('<br/>')
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLabel: { fontSize: 10 },
        boundaryGap: false, 
      },
      yAxis: [Y_AXIS_STATUS_CONFIG, getYAxisJobsConfig(yAxisMaxValue)],
      series: series.map(formatSeriesItem)
    };
  }, [xAxisData, series]);

  const finalCards = cards?.length ? cards : [];

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="h4"
        sx={{ fontWeight: 500, fontSize: '30px', mb: 2, color: 'primary.main' }}
      >
        {title}
      </Typography>

      <Box sx={{ height: 350, width: '100%', position: 'relative' }}>
        {isLoading ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LoaderComponent />
          </Box>
        ) : (
          <ReactECharts option={chartOption} style={{ height: '100%', width: '100%' }} />
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mt: 4 }}>
        {(isCardsLoading
          ? [{ label: 'Total Run Status' }, { label: 'Peak Day' }, { label: 'Avg/Day' }]
          : finalCards
        ).map((card: any, index: number) => (
          <StatCard key={card?.label || index} {...card} isLoading={isCardsLoading} />
        ))}
      </Box>
    </Box>
  );
};

export default TrendLineInfoWidget;
